import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axios';
import Wrapper from '../../components/Wrapper';

// Hardcoded parameters
const PARAMETERS = {
  SOIL: ['ec', 'oc', 'phosphorus', 'potassium', 'potassium_new', 'ammonia', 'nitrate', 'calcium', 'magnesium', 'sulphur', 'boron', 'iron', 'copper', 'zinc'],
  WATER: ['ec', 'phosphorus', 'potassium', 'ammonia', 'nitrate', 'calcium', 'magnesium', 'sulphur', 'boron', 'iron', 'copper', 'zinc', 'carbonate', 'bicarbonate']
};

const ParameterFormula = () => {
  const [sampleType, setSampleType] = useState('SOIL');
  const [parameterName, setParameterName] = useState(PARAMETERS.SOIL[0]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  // Update parameter dropdown when sample type changes
  useEffect(() => {
    setParameterName(PARAMETERS[sampleType][0]);
  }, [sampleType]);

  useEffect(() => {
    const fetchRules = async () => {
      setLoading(true);
      setAlert({ show: false, message: '', type: '' });
      try {
        const response = await axiosInstance.get(`/parameter-formulas/${sampleType}/${parameterName}`);
        let fetchedRules = response.data.data || [];
        
        // If no rules exist, initialize with a default fallback rule
        if (fetchedRules.length === 0) {
          fetchedRules = [{ condition_expression: 'true', formula_expression: 'value', decimal_places: 2 }];
        }
        setRules(fetchedRules);
      } catch (error) {
        setAlert({ show: true, message: 'Failed to fetch formulas.', type: 'danger' });
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, [sampleType, parameterName]);

  const handleSave = async () => {
    setSaving(true);
    setAlert({ show: false, message: '', type: '' });
    try {
      await axiosInstance.put(`/parameter-formulas/${sampleType}/${parameterName}`, { rules });
      setAlert({ show: true, message: 'Rules successfully updated!', type: 'success' });
    } catch (error) {
      const errMsg = error.response?.data?.message || 'Error saving formulas.';
      setAlert({ show: true, message: errMsg, type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  const handleRuleChange = (index, field, value) => {
    const updatedRules = [...rules];
    updatedRules[index][field] = value;
    setRules(updatedRules);
  };

  const addRule = () => {
    const newRules = [...rules];
    newRules.splice(newRules.length - 1, 0, {
      condition_expression: 'value == 0',
      formula_expression: '0',
      decimal_places: 2
    });
    setRules(newRules);
  };

  const deleteRule = (index) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    setRules(updatedRules);
  };

  const moveRule = (index, direction) => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === rules.length - 2)
    ) return;

    const newRules = [...rules];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newRules[index], newRules[swapIndex]] = [newRules[swapIndex], newRules[index]];
    setRules(newRules);
  };

  return (
    <Wrapper>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Formula Editor</h4>
        <button 
          className="btn btn-primary" 
          onClick={handleSave} 
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {alert.show && (
        <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
          {alert.message}
          <button type="button" className="btn-close" onClick={() => setAlert({ show: false })}></button>
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-body bg-light">
          <div className="row">
            <div className="col-md-6 mb-3 mb-md-0">
              <label className="form-label fw-bold">Sample Type</label>
              <select 
                className="form-select" 
                value={sampleType} 
                onChange={(e) => setSampleType(e.target.value)}
              >
                <option value="SOIL">SOIL</option>
                <option value="WATER">WATER</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">Parameter</label>
              <select 
                className="form-select" 
                value={parameterName} 
                onChange={(e) => setParameterName(e.target.value)}
              >
                {PARAMETERS[sampleType].map(param => (
                  <option key={param} value={param}>{param.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : (
        <div className="rules-container">
          {rules.map((rule, index) => {
            const isLastRule = index === rules.length - 1;

            return (
              <div key={index} className={`card mb-3 ${isLastRule ? 'border-primary' : ''}`}>
                <div className="card-header d-flex justify-content-between align-items-center bg-white">
                  <span className="fw-bold text-muted">
                    {isLastRule ? 'Fallback Rule (Else)' : `Condition Rule #${index + 1}`}
                  </span>
                  <div>
                    {!isLastRule && (
                      <>
                        <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => moveRule(index, 'up')} disabled={index === 0}>↑</button>
                        <button className="btn btn-sm btn-outline-secondary me-3" onClick={() => moveRule(index, 'down')} disabled={index === rules.length - 2}>↓</button>
                        <button className="btn btn-sm btn-danger" onClick={() => deleteRule(index)}>Delete</button>
                      </>
                    )}
                  </div>
                </div>
                <div className="card-body row align-items-center">
                  
                  {/* Condition Expression */}
                  <div className="col-md-4 mb-2 mb-md-0">
                    <label className="form-label small">If Condition is:</label>
                    <input 
                      type="text" 
                      className={`form-control ${isLastRule ? 'bg-light' : ''}`}
                      value={rule.condition_expression} 
                      onChange={(e) => handleRuleChange(index, 'condition_expression', e.target.value)}
                      disabled={isLastRule}
                      placeholder="e.g., value > 0"
                    />
                  </div>

                  {/* Formula Expression */}
                  <div className="col-md-6 mb-2 mb-md-0">
                    <label className="form-label small">Execute Formula:</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={rule.formula_expression} 
                      onChange={(e) => handleRuleChange(index, 'formula_expression', e.target.value)}
                      placeholder="e.g., (value * 0.37) + 0.55"
                    />
                  </div>

                  {/* Decimal Places */}
                  <div className="col-md-2">
                    <label className="form-label small">Decimals:</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={rule.decimal_places} 
                      min="0"
                      max="5"
                      onChange={(e) => handleRuleChange(index, 'decimal_places', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <button className="btn btn-outline-primary mt-2" onClick={addRule}>
            + Add New Condition
          </button>
        </div>
      )}
    </Wrapper>
  );
};

export default ParameterFormula;